import { KeyboardEvent } from "react";
import {
	FocusEvent,
	useState,
	isValidElement,
	cloneElement,
	ReactElement,
} from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";

import {
	INPUT_LABEL_SIZE,
	INPUT_LABEL_SIZE_FOCUSED,
	LABEL_PADDING_X,
	StyledContainer,
	StyledFieldset,
	StyledLabel,
	StyledLegend,
	StyledMessage,
} from "./TextInputContainer.styles";

import { TextInputContainerProps } from "./TextInputContainer.types";
import Box from "../../layout/boxes/Box";

const TextInputContainer: React.FC<TextInputContainerProps> = ({
	id,
	label,
	required,
	colorOn = "bg_white",
	state = "default",
	value,
	disabled,
	message,
	width,
	className,
	children,
	onBlur,
	onFocus,
	showFocusedLabel = false,
	...props
}) => {
	const block = new BemIt("TextInputContainer");

	const [isFocused, setIsFocused] = useState<boolean>(false);
	const hasLabel = Boolean(label);
	const hasValue = value ? value.toString().length > 0 : false;
	const labelId = id ? `${id}-label` : undefined;
	const isFocusedLabel = hasValue || isFocused || showFocusedLabel;
	const labelSize = isFocusedLabel
		? INPUT_LABEL_SIZE_FOCUSED
		: INPUT_LABEL_SIZE;
	const boldedLabel = isFocusedLabel ? true : false;
	const resolvedLabelPaddingX = isFocusedLabel ? LABEL_PADDING_X : "0";

	const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
		setIsFocused(true);
		if (onFocus) onFocus(e);
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		setIsFocused(false);
		if (onBlur) onBlur(e);
	};

	const inputElement =
		isValidElement(children) &&
		cloneElement(children as ReactElement, {
			value,
			colorOn,
			required,
			disabled,
			hasLabel,
			onFocus: handleFocus,
			onBlur: handleBlur,
			...(id && {
				id,
				"aria-labelledby": labelId,
			}),
			...props,
		});

	const handleBoxKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.code === "ArrowDown") {
		}
	};

	return (
		<StyledContainer className={addClass(className).after(block)} width={width}>
			<Box position="relative" onKeyDown={handleBoxKeyDown}>
				{inputElement}
				<StyledLabel
					className="placeholder-text"
					htmlFor={id}
					id={labelId}
					size={labelSize}
					paddingX={resolvedLabelPaddingX}
					bold={boldedLabel}
					required={required}
					colorOn={colorOn}
					focused={isFocusedLabel}
				>
					{label}
				</StyledLabel>
				<StyledFieldset
					aria-hidden="true"
					hasLabel={hasLabel}
					focused={isFocused}
					state={state}
					disabled={disabled}
					colorOn={colorOn}
				>
					<StyledLegend
						htmlTag="legend"
						size="sm"
						paddingX={resolvedLabelPaddingX}
						required={required}
						focused={isFocused || showFocusedLabel}
						hasValue={hasValue}
					>
						{label}
					</StyledLegend>
				</StyledFieldset>
			</Box>
			{message && (
				<StyledMessage
					left={0}
					variant="body-small"
					colorOn={colorOn}
					colorContrast={1}
					state={state}
				>
					{message}
				</StyledMessage>
			)}
		</StyledContainer>
	);
};

export default TextInputContainer;
